/*
  Customisation request
  ---------------------
  Enables the fields when the box is ticked, mirrors the colour dropdowns into
  the hidden inputs that actually carry the properties, and stops the customer
  adding to cart with the box ticked and nothing asked for.

  Why the colour dropdowns are unnamed and mirrored
  -------------------------------------------------
  A select the customer interacts with cannot be disabled (they could never
  change it back), but "No change" must post nothing at all, and the Custom
  option must post the typed code rather than a "__custom__" sentinel. So each
  dropdown is a UI control only, and its effective value is written into a
  hidden input that is enabled exactly when there is something to say.

  Why the submit guard is not just `required`
  -------------------------------------------
  The add to cart form is rendered with novalidate (buy-buttons.liquid), and
  product-form.js calls preventDefault() and goes straight to fetch without
  ever asking the form whether it is valid, so the browser never runs
  constraint validation on this form and `required` alone stops nothing.

  The guard listens on document in the CAPTURE phase. product-form.js binds its
  own submit listener on the form element itself, and a capture listener on an
  ancestor runs before the event reaches the target, so stopPropagation() here
  stops the add ever being attempted. Binding capture on the form instead would
  not be enough: at the target element listeners fire in registration order
  regardless of the capture flag, and product-form.js registers first.

  The form is matched by id at event time rather than held as a reference,
  because a combined listing product swap replaces the whole product-info and
  with it the form, while the id stays the same.

  Dynamic checkout is a second way out of the page. The Buy it now button
  submits through Shopify's own script rather than the form's submit event, so
  the guard above never sees it. It is made unusable while the request is
  incomplete instead.

  State survives an ordinary variant change: product-info.js replaces the
  price, sku, inventory, volume and quantity nodes by id and view-transitions
  variant-selects, none of which contains this block. It does not survive a
  product swap, where <main> is replaced outright.
*/
if (!customElements.get('ww-customisation-field')) {
  customElements.define(
    'ww-customisation-field',
    class WwCustomisationField extends HTMLElement {
      // Children are read in connectedCallback rather than the constructor.
      // Quick add and product swap both rebuild this markup through
      // HTMLUpdateUtility.setInnerHTML, where an upgrade can reach the
      // constructor before the children are in place.
      connectedCallback() {
        if (!this.checkbox) this.init();
        if (!this.checkbox) return;

        this.submitGuard = this.onSubmitCapture.bind(this);
        document.addEventListener('submit', this.submitGuard, true);

        // A second, independent interception. The submit guard only ever sees
        // routes that fire a submit event, and dynamic checkout is not one of
        // them. Capture-phase click also catches keyboard activation, since
        // Enter and Space on a button dispatch a click.
        this.clickGuard = this.onClickCapture.bind(this);
        document.addEventListener('click', this.clickGuard, true);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source !== 'product-form') return;

            // Only reset for the add that came from our own form. The variant
            // input still holds the variant that was added at this point.
            const variantInput = this.form && this.form.querySelector('[name="id"]');
            if (!variantInput || String(event.productVariantId) !== String(variantInput.value)) return;

            this.reset();
          });
        }
      }

      disconnectedCallback() {
        if (this.submitGuard) document.removeEventListener('submit', this.submitGuard, true);
        if (this.clickGuard) document.removeEventListener('click', this.clickGuard, true);
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      init() {
        // Scoped to the block, not the section: several of these can sit on
        // one section (one per customisation tier), so a section-only id
        // would have them all answering to the same selectors.
        const id = this.dataset.uid;

        this.checkbox = this.querySelector(`#WwCustomisation-checkbox-${id}`);
        // Every input below is optional: each is a block setting the merchant
        // can switch off, so a block may offer a colour and no text, text and
        // no colour, or any combination.
        this.input = this.querySelector(`#WwCustomisation-input-${id}`);
        this.personalisation = this.querySelector('[data-ww-personalisation]');
        this.errorMessage = this.querySelector(`#WwCustomisation-error-${id}`);
        this.liveRegion = this.querySelector(`#WwCustomisation-live-region-${id}`);
        this.marker = this.querySelector(`#WwCustomisation-marker-${id}`);
        // Markers name which input wrote which property, for the uploader app.
        // Each follows its own input exactly, so a marker posts only when the
        // thing it describes was actually filled in.
        this.textMarker = this.querySelector('[data-ww-text-marker]');
        this.personalisationMarker = this.querySelector('[data-ww-personalisation-marker]');
        this.colourGroups = [...this.querySelectorAll('[data-ww-colour]')].map((wrap) => ({
          select: wrap.querySelector('[data-ww-colour-select]'),
          codeWrap: wrap.querySelector('[data-ww-colour-code-wrap]'),
          code: wrap.querySelector('[data-ww-colour-code]'),
          value: wrap.querySelector('[data-ww-colour-value]'),
          marker: wrap.querySelector('[data-ww-colour-marker]'),
        }));
        if (!this.checkbox) return;

        // One listener pair covers the checkbox, every dropdown and code
        // input, and the textarea; everything routes through update().
        this.wasChecked = this.checkbox.checked;
        this.addEventListener('change', this.update.bind(this));
        this.addEventListener('input', this.update.bind(this));

        this.update();
      }

      get form() {
        return document.getElementById(this.dataset.formId);
      }

      // The value a colour group is actually asking for: the chosen name, the
      // typed code when Custom is selected, or nothing.
      effectiveColour(group) {
        if (!group.select) return '';
        if (group.select.value === '__custom__') return group.code ? group.code.value.trim() : '';
        return group.select.value;
      }

      get hasChange() {
        if (this.colourGroups.some((g) => this.effectiveColour(g) !== '')) return true;
        if (this.personalisation && this.personalisation.value.trim() !== '') return true;
        return !!this.input && this.input.value.trim() !== '';
      }

      // Where to send the customer when they submit an empty request. The
      // text field is the obvious place to start typing, but a block can be
      // configured without one, in which case the first colour control is.
      get firstControl() {
        if (this.personalisationRequired) return this.personalisation;
        if (this.input) return this.input;
        if (this.personalisation) return this.personalisation;
        const group = this.colourGroups[0];
        if (!group) return null;
        return group.select.closest('ww-colour-select')?.querySelector('.ww-colour-select__button') || group.select;
      }

      // True when the request is ticked but says nothing. On a product where
      // personalisation is required, an empty personalisation is incomplete
      // however much else has been chosen: a name is not optional just
      // because a colour was picked too.
      get isIncomplete() {
        if (!this.checkbox.checked) return false;
        if (this.personalisationRequired && this.personalisation.value.trim() === '') return true;
        return !this.hasChange;
      }

      get personalisationRequired() {
        return !!this.personalisation && this.hasAttribute('data-personalisation-required');
      }

      update() {
        const on = this.checkbox.checked;

        // A disabled field is not submitted, so an unticked box posts no
        // properties rather than empty ones.
        if (this.input) this.input.disabled = !on;
        if (this.personalisation) this.personalisation.disabled = !on;
        if (this.marker) this.marker.disabled = !on;

        // The text fields post whatever they hold, empty included; their
        // markers do not. A marker claims "this input asked for something",
        // which an empty box has not.
        if (this.textMarker) {
          this.textMarker.disabled = !on || !this.input || this.input.value.trim() === '';
        }
        if (this.personalisationMarker) {
          this.personalisationMarker.disabled =
            !on || !this.personalisation || this.personalisation.value.trim() === '';
        }

        this.colourGroups.forEach((group) => {
          const custom = group.select && group.select.value === '__custom__';
          if (group.codeWrap) group.codeWrap.hidden = !custom;
          const effective = this.effectiveColour(group);
          if (group.value) {
            group.value.value = effective;
            group.value.disabled = !on || effective === '';
          }
          // Same condition as the value above, so the pair always travels
          // together or not at all.
          if (group.marker) group.marker.disabled = !on || effective === '';
        });

        // Clear the moment the request becomes complete, rather than making
        // them submit again to find out the error has gone.
        if (!on || this.hasChange) this.clearError();

        if (on !== this.wasChecked) {
          this.wasChecked = on;
          if (this.liveRegion) {
            this.liveRegion.textContent = on
              ? this.dataset.expandedText || ''
              : this.dataset.collapsedText || '';
          }
        }

        this.updatePaymentButton();
      }

      // Is this a form the gate should apply to? Our own add to cart form,
      // or any other cart-add form in the same section. main-product.liquid
      // renders a second one for Shop Pay instalment messaging
      // (product-form-installment-SECTION), and a second form must not be a
      // way around the gate even though our inputs do not associate with it.
      guards(form) {
        if (!(form instanceof HTMLFormElement)) return false;
        if (form.getAttribute('id') === this.dataset.formId) return true;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return false;
        const section = this.closest('.shopify-section');
        return !!section && section.contains(form);
      }

      onSubmitCapture(event) {
        // getAttribute('id'), not .id. The form holds <input name="id"> for the
        // variant, and named controls are exposed as properties on the form
        // element, so form.id is that input rather than the string. Comparing
        // against .id silently never matches and the guard never fires.
        if (!this.guards(event.target)) return;
        if (this.isIncomplete) {
          event.preventDefault();
          event.stopPropagation();
          this.showError();
          this.firstControl?.focus();
          return;
        }

        // Colour chosen but nothing typed: the textarea is enabled (the
        // customer could still type) yet empty, and an enabled empty field
        // posts "Customisation request: ". Disable it for the duration of
        // this submit so the property is absent rather than blank. This
        // capture listener runs before product-form.js builds its FormData
        // in the same dispatch; the timeout restores state afterwards.
        // An enabled but empty field posts a blank property. Disable whichever
        // are empty for the duration of this submit so they are absent rather
        // than blank. This capture listener runs before product-form.js builds
        // its FormData in the same dispatch; the timeout restores state after.
        let blanked = false;
        if (this.checkbox.checked) {
          [this.input, this.personalisation].forEach((el) => {
            if (el && el.value.trim() === '') {
              el.disabled = true;
              blanked = true;
            }
          });
        }
        // A required personalisation never reaches here empty: isIncomplete
        // above has already stopped the submit.
        if (blanked) setTimeout(() => this.update(), 0);
      }

      // Blocks activation of the buy controls while the request is incomplete,
      // whether that activation came from a pointer or the keyboard. Clicks
      // inside a cross-origin wallet iframe never reach this document, which
      // is why updatePaymentButton makes that button inert as well.
      onClickCapture(event) {
        if (!this.isIncomplete) return;
        const form = this.form;
        if (!form || !(event.target instanceof Element)) return;

        const control = event.target.closest('[name="add"], .shopify-payment-button');
        if (!control || !form.contains(control)) return;

        event.preventDefault();
        event.stopPropagation();
        this.showError();
        this.firstControl?.focus();
      }

      showError() {
        if (!this.errorMessage) return;
        this.errorMessage.classList.remove('hidden');
        if (!this.input) return;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setAttribute('aria-describedby', this.errorMessage.id);
      }

      clearError() {
        if (!this.errorMessage) return;
        this.errorMessage.classList.add('hidden');
        if (!this.input) return;
        this.input.setAttribute('aria-invalid', 'false');
        this.input.removeAttribute('aria-describedby');
      }

      updatePaymentButton() {
        const form = this.form;
        const button = form && form.querySelector('.shopify-payment-button');
        if (!button) return;

        const blocked = this.isIncomplete;
        button.classList.toggle('ww-customisation-blocked', blocked);
        button.setAttribute('aria-disabled', blocked ? 'true' : 'false');

        // The class alone was not enough. Buy it now goes straight to checkout
        // through Shopify's own script without ever firing a submit event, so
        // the submit guard never sees it and this button is the whole defence.
        // pointer-events stops a mouse and nothing else: aria-disabled is
        // advisory, and tabbing to the button and pressing Enter went through.
        // inert takes it out of hit testing, the tab order and the
        // accessibility tree, and applies inside the wallet iframe too.
        // The class stays as the visual treatment and as the fallback on any
        // browser without inert.
        button.inert = blocked;
      }

      reset() {
        if (!this.checkbox.checked) return;
        // Where the request is required the box stays ticked: unticking it
        // would hide the very field the next add to cart still needs, and
        // the customer would meet a refusal with nothing on screen to fix.
        if (!this.personalisationRequired) this.checkbox.checked = false;
        if (this.input) this.input.value = '';
        if (this.personalisation) this.personalisation.value = '';
        this.colourGroups.forEach((group) => {
          if (group.select) {
            group.select.value = '';
            // Dispatched, not just assigned, so the ww-colour-select button
            // showing the old choice hears about it and refreshes.
            group.select.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (group.code) group.code.value = '';
        });
        this.update();
      }
    }
  );
}

/*
  Swatch dropdown
  ---------------
  Progressive enhancement over the native select in ww-colour-select.liquid.
  A native select cannot render colour in its options (the browser owns that
  popup), so this hides it and draws a button plus listbox with a swatch dot
  per colour, taken from each option's data-swatch.

  The native select stays in the DOM as the single source of truth: choosing
  an option here sets select.value and dispatches change, so the mirroring,
  validation and reset logic above never know the enhancement exists, and
  with no JavaScript the plain select still works.
*/
if (!customElements.get('ww-colour-select')) {
  customElements.define(
    'ww-colour-select',
    class WwColourSelect extends HTMLElement {
      connectedCallback() {
        if (this.button) return;
        this.native = this.querySelector('select');
        if (!this.native) return;

        this.button = document.createElement('button');
        this.button.type = 'button';
        this.button.className = 'ww-colour-select__button';
        this.button.id = `${this.native.id}-button`;
        this.button.setAttribute('aria-haspopup', 'listbox');
        this.button.setAttribute('aria-expanded', 'false');

        this.buttonDot = this.makeDot(null);
        this.buttonText = document.createElement('span');
        this.buttonText.className = 'ww-colour-select__button-text';
        const caret = document.createElement('span');
        caret.className = 'ww-colour-select__caret svg-wrapper';
        caret.innerHTML = this.querySelector('.select .svg-wrapper')?.innerHTML || '';
        this.button.append(this.buttonDot, this.buttonText, caret);

        this.listbox = document.createElement('ul');
        this.listbox.className = 'ww-colour-select__listbox';
        this.listbox.setAttribute('role', 'listbox');
        this.listbox.hidden = true;

        [...this.native.options].forEach((opt) => {
          const li = document.createElement('li');
          li.setAttribute('role', 'option');
          li.dataset.value = opt.value;
          li.tabIndex = -1;
          li.append(
            this.makeDot(opt.value === '__custom__' ? '__custom__' : opt.dataset.swatch || null),
            document.createTextNode(opt.textContent.trim())
          );
          this.listbox.append(li);
        });

        this.append(this.button, this.listbox);
        this.classList.add('ww-colour-select--enhanced');

        // The label points at the hidden native select; repoint it so
        // clicking it focuses the control the customer actually uses.
        const label = this.closest('[data-ww-colour]')?.querySelector(`label[for="${this.native.id}"]`);
        if (label) {
          label.htmlFor = this.button.id;
          this.button.setAttribute('aria-label', label.textContent.trim());
        }

        this.button.addEventListener('click', () => (this.listbox.hidden ? this.open() : this.close(true)));
        this.button.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.open();
          }
        });
        this.listbox.addEventListener('keydown', this.onListKeydown.bind(this));
        this.listbox.addEventListener('click', (e) => {
          const li = e.target.closest('[role="option"]');
          if (li) this.choose(li);
        });
        this.onOutsideClick = (e) => {
          if (!this.contains(e.target)) this.close(false);
        };
        this.native.addEventListener('change', () => this.refresh());

        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onOutsideClick);
      }

      makeDot(swatch) {
        const dot = document.createElement('span');
        dot.className = 'ww-colour-select__dot';
        if (swatch === '__custom__') dot.classList.add('ww-colour-select__dot--custom');
        else if (swatch) dot.style.backgroundColor = swatch;
        else dot.classList.add('ww-colour-select__dot--none');
        return dot;
      }

      options() {
        return [...this.listbox.querySelectorAll('[role="option"]')];
      }

      open() {
        this.listbox.hidden = false;
        this.button.setAttribute('aria-expanded', 'true');
        document.addEventListener('click', this.onOutsideClick);
        const current = this.options().find((li) => li.dataset.value === this.native.value) || this.options()[0];
        current?.focus();
      }

      close(focusButton) {
        this.listbox.hidden = true;
        this.button.setAttribute('aria-expanded', 'false');
        document.removeEventListener('click', this.onOutsideClick);
        if (focusButton) this.button.focus();
      }

      onListKeydown(event) {
        const opts = this.options();
        const index = opts.indexOf(document.activeElement);
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            opts[Math.min(index + 1, opts.length - 1)]?.focus();
            break;
          case 'ArrowUp':
            event.preventDefault();
            opts[Math.max(index - 1, 0)]?.focus();
            break;
          case 'Home':
            event.preventDefault();
            opts[0]?.focus();
            break;
          case 'End':
            event.preventDefault();
            opts[opts.length - 1]?.focus();
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            if (index >= 0) this.choose(opts[index]);
            break;
          case 'Escape':
            event.preventDefault();
            this.close(true);
            break;
          case 'Tab':
            this.close(false);
            break;
        }
      }

      choose(li) {
        this.native.value = li.dataset.value;
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
        this.close(true);
      }

      refresh() {
        const selected = this.native.selectedOptions[0];
        this.buttonText.textContent = selected ? selected.textContent.trim() : '';
        const swatch = selected && selected.value === '__custom__'
          ? '__custom__'
          : (selected && selected.dataset.swatch) || null;
        const fresh = this.makeDot(swatch);
        this.buttonDot.replaceWith(fresh);
        this.buttonDot = fresh;
        this.options().forEach((li) => {
          li.setAttribute('aria-selected', li.dataset.value === this.native.value ? 'true' : 'false');
        });
      }
    }
  );
}
